import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import "./modal-dialog.css";

type Props = {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  placement: "centre" | "top";
  /** How wide the panel may grow, in rem. The panel owns its width, not its contents. */
  width?: number;
  children: ReactNode;
};

/**
 * A native modal dialog: the browser traps focus, makes the page behind it inert and
 * closes on Escape. Clicking outside the panel also closes it, through a real button that
 * fills the backdrop; it is kept out of the tab order because the visible Close button
 * already serves the keyboard.
 */
export function ModalDialog({ open, onClose, labelledBy, placement, width, children }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={dialog}
      className="modal-dialog"
      data-placement={placement}
      aria-labelledby={labelledBy}
      onClose={onClose}
    >
      <div
        className="modal-dialog__panel"
        style={width ? ({ "--modal-width": `${String(width)}rem` } as CSSProperties) : undefined}
      >
        {children}
      </div>
      {/* Clicking outside the panel closes it. It comes last and sits behind, so the
          browser's own dialog focus lands on the first control inside the panel. */}
      <button
        type="button"
        className="modal-dialog__backdrop"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
      />
    </dialog>
  );
}
